import { useEffect } from "react";
import { ArrowUpRight } from "lucide-react";
import { Reveal, TrackCorner } from "../components/krida/primitives";

const PAGES = {
  academies: {
    image: "/images/sol-academies.jpg",
    eyebrow: "For Academies & Clubs",
    h1: "Every programme runs on verified professionals.",
    sub: "Seasonal staffing, credential checks and fragmented records — replaced by one structured workforce system for your academy or club.",
    stages: ["Discover", "Verify", "Deploy"],
    sections: [
      { h: "Verified coaching bench", body: "Identity, certifications and experience checked before anyone works with your athletes — coaches, S&C professionals and support staff alike." },
      { h: "Seasonal & year-round staffing", body: "Temporary camps, seasonal programmes and permanent roles filled from a structured pool, not last-minute calls." },
      { h: "One record per professional", body: "Deployment history, ratings and availability in a single verified profile that stays current across seasons." },
      { h: "Safer programmes", body: "Credential flags and compliance checks for everyone working around youth and academy athletes." },
    ],
  },
  schools: {
    image: "/images/sol-schools.jpg",
    eyebrow: "For Schools & Universities",
    h1: "Inter-school and collegiate sport, professionally staffed.",
    sub: "Verified coaches, officials and medical cover for fixtures, tournaments and programmes — without the administrative burden.",
    stages: ["Discover", "Verify", "Deploy"],
    sections: [
      { h: "Verified coaches & officials", body: "Every coach, referee and official arrives with identity, certification and experience already checked." },
      { h: "Fixture & tournament staffing", body: "Staff inter-school fixtures, sports days and collegiate tournaments from one pool — scorers, timekeepers and medical cover included." },
      { h: "Compliance for youth sport", body: "Safeguarding-aware verification and clear records for everyone working with students." },
      { h: "Simple scheduling", body: "Availability, assignments and attendance handled digitally — no spreadsheet chains between departments." },
    ],
  },
  venues: {
    image: "/images/sol-venues.jpg",
    eyebrow: "For Venues & Stadiums",
    h1: "Your venue's workforce, coordinated.",
    sub: "Event-day operations, accreditation, technical and support teams — scheduled, verified and managed in one place.",
    stages: ["Deploy", "Manage", "Progress"],
    sections: [
      { h: "Event-day workforce", body: "Operations, hospitality and support personnel planned against each event's actual requirement." },
      { h: "Accreditation & access", body: "Registration and accreditation staff with verified identity — controlling who gets in, and where." },
      { h: "Technical & grounds teams", body: "Technical crews, grounds and facility teams scheduled across your event calendar." },
      { h: "Attendance & records", body: "Digital attendance and a deployment record for every shift, ready for audit and billing." },
    ],
  },
  federations: {
    image: "/images/sol-federations.jpg",
    eyebrow: "For Federations & Associations",
    h1: "Build the workforce your sport depends on.",
    sub: "Structured professional pools, credential oversight and opportunity creation — for every role that keeps your sport running.",
    stages: ["Verify", "Skill", "Progress"],
    sections: [
      { h: "Structured professional pools", body: "Maintain living pools of coaches, officials, technical personnel, medical and performance staff, tournament teams, administrators and volunteers — organised by role, region, level and availability." },
      { h: "Credential oversight", body: "Certification tracking, grades and compliance flags across your regions, so every appointment rests on a verified record." },
      { h: "Opportunity creation", body: "Publish assignments, courses and development pathways directly to your professional pool — from grassroots fixtures to national championships." },
      { h: "Workforce intelligence", body: "See supply by role, district and certification level, and spot gaps before they reach the field of play." },
    ],
  },
  government: {
    image: "/images/sol-government.jpg",
    eyebrow: "For Government",
    h1: "Sports workforce infrastructure for public missions.",
    sub: "Krida complements existing athlete-registration and competition systems as the workforce layer — verified, compliant and built for scale.",
    stages: ["Verify", "Skill", "Progress"],
    sections: [
      { h: "Workforce visibility", body: "Understand the available sports workforce by sport, district, role, qualification, certification, experience and availability." },
      { h: "Workforce development", body: "Identify skill shortages, certification requirements, regional gaps and training demand — and target programmes where they matter." },
      { h: "Deployment", body: "See where professionals are being utilised across programmes, venues and events, with assignments and attendance in one system of record." },
      { h: "Outcomes", body: "Track people registered, verified, trained and deployed — with participation and geographic distribution reported as registries connect." },
    ],
  },
  "event-workforce": {
    image: "/images/sol-event.jpg",
    eyebrow: "For Event Workforce",
    h1: "Every event runs on its workforce.",
    sub: "From a single fixture to a multi-venue tournament — verified people, coordinated schedules and a record of every assignment.",
    stages: ["Deploy", "Manage", "Progress"],
    sections: [
      { h: "Event-scale planning", body: "Scorers, timekeepers, competition managers, venue staff, event personnel, volunteers and logistics — planned against the actual requirement." },
      { h: "Verification at scale", body: "Identity, credentials and certifications checked before anyone reaches the venue — not at the gate." },
      { h: "Scheduling & attendance", body: "Digital scheduling, availability confirmation and attendance tracking across venues and fixtures." },
      { h: "Post-event record", body: "Every assignment becomes verified work history for the professional and a deployment record for the organiser." },
    ],
    numbers: [
      { n: "28", label: "Officials" },
      { n: "14", label: "Scorers & Timekeepers" },
      { n: "10", label: "Physiotherapists" },
      { n: "6", label: "Competition Managers" },
      { n: "24", label: "Venue Staff" },
      { n: "40", label: "Event Personnel" },
      { n: "60", label: "Volunteers" },
      { n: "12", label: "Logistics" },
    ],
  },
};

export default function SolutionPage({ page }) {
  const P = PAGES[page];

  useEffect(() => {
    document.title = `${P.h1} — CYRA Krida`;
    return () => { document.title = "CYRA Krida — The Sports Workforce OS"; };
  }, [P.h1]);

  return (
    <div className="min-h-screen bg-white" data-testid={`solution-${page}`}>
      {/* header */}
      <header className="noise relative overflow-hidden bg-kn-navy">
        <img src={P.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-r from-kn-navy via-kn-navy/70 to-kn-navy/30" />
        <div className="pointer-events-none absolute -right-24 -top-24 text-kn-cyan/15">
          <TrackCorner className="h-[300px] w-[300px] rotate-90" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 pb-20 pt-10 lg:px-10">
          <a href="/" className="flex w-fit items-center gap-3" data-testid="solution-home-link">
            <img src="/logo-white-wm.png" alt="CYRA" className="h-7 w-auto" />
            <span className="h-5 w-px bg-white/25" />
            <span className="font-serif text-lg italic leading-none text-kn-cyan">Krida</span>
          </a>
          <div className="mt-20 max-w-3xl">
            <p className="flex items-center gap-4 font-mono text-[11px] tracking-[0.32em] uppercase text-kn-cyan">
              <span className="h-px w-10 bg-kn-cyan/60" />
              {P.eyebrow}
            </p>
            <h1 className="mt-6 text-4xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl">
              {P.h1}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-white/65">{P.sub}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {P.stages.map((s) => (
                <span key={s} className="rounded-full border border-kn-cyan/30 px-4 py-1.5 font-mono text-[10px] uppercase tracking-wider text-kn-cyan">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </header>

      {/* sections */}
      <main className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
        <div className="grid gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 sm:grid-cols-2">
          {P.sections.map((s, i) => (
            <Reveal key={s.h} delay={i * 0.08} className="h-full">
              <div className="group h-full bg-white p-10 transition-colors duration-500 hover:bg-kn-navy">
                <span className="font-mono text-xs text-kn-blue transition-colors duration-500 group-hover:text-kn-cyan">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-4 text-xl font-semibold text-kn-navy transition-colors duration-500 group-hover:text-white">{s.h}</h2>
                <p className="mt-3 text-sm leading-relaxed text-kn-navy/60 transition-colors duration-500 group-hover:text-white/60">{s.body}</p>
              </div>
            </Reveal>
          ))}
        </div>

        {P.numbers && (
          <Reveal className="mt-20">
            <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-kn-navy/45">One state tournament may need</p>
            <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-kn-navy/10 bg-kn-navy/10 sm:grid-cols-4" data-testid="solution-numbers">
              {P.numbers.map((u) => (
                <div key={u.label} className="bg-kn-ice p-6 text-center">
                  <p className="text-3xl font-semibold text-kn-navy">{u.n}</p>
                  <p className="mt-1.5 font-mono text-[9px] uppercase tracking-wider text-kn-navy/50">{u.label}</p>
                </div>
              ))}
            </div>
          </Reveal>
        )}

        {/* CTA */}
        <Reveal className="mt-20">
          <div className="bg-tile-gradient flex flex-col items-start justify-between gap-8 rounded-2xl p-10 sm:flex-row sm:items-center lg:p-14">
            <div>
              <p className="font-mono text-[11px] tracking-[0.28em] uppercase text-kn-cyan">CYRA Krida</p>
              <p className="mt-3 max-w-md font-serif text-2xl italic leading-snug text-white">
                Sport runs on people. Krida connects the workforce behind it.
              </p>
            </div>
            <a
              href="/#contact"
              data-testid="solution-cta"
              className="group inline-flex items-center gap-3 rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5"
            >
              Talk to Krida
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </a>
          </div>
        </Reveal>
      </main>

      <footer className="border-t border-kn-navy/10">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-6 py-8 font-mono text-[10px] uppercase tracking-[0.2em] text-kn-navy/35 sm:flex-row lg:px-10">
          <span>© 2026 CYRA Krida</span>
          <span>The Sports Workforce OS</span>
          <span>Singapore · Malaysia · India</span>
        </div>
      </footer>
    </div>
  );
}
